<template>
  <div class="wdw-map" :id="id" :style="mapStyle" ref="wdwMap">
    <div class="wdw-map-balloon" :id="popupId">
      <a class="close" @click="hidePopup()"></a>
      <div class="content"></div>
      <button
        v-if="openedFeature"
        type="button"
        class="btn btn-primary center-block infobox-button"
        @click="emitOpenFeature(openedFeature.id)"
        v-dompurify-html="openedFeature.buttonTitle"
      ></button>
    </div>
  </div>
  <div id="wdw-bms-map-location-result" role="alert" tabindex="0"></div>
</template>

<script>
// import debounce from 'debounce';
import {
  Vector as source_vector,
  TileWMS as source_tilewms,
  TileImage as source_tileimage,
  OSM as source_osm,
  XYZ as source_xyz,
  Cluster as source_cluster,
} from 'ol/source';
import { Vector as layer_vector, Tile as layer_tile, Group as layer_group } from 'ol/layer';
import { Point as geom_point } from 'ol/geom';
import { GeoJSON } from 'ol/format';
import {
  Style as ol_style,
  Icon as ol_icon,
  Fill as ol_fill,
  Stroke as ol_stroke,
  Circle as ol_circle,
  Text as ol_text,
} from 'ol/style';
import { View as ol_view, Map as ol_map, Overlay as ol_overlay, Feature as ol_feature } from 'ol';
import * as ol_control from 'ol/control';
import * as ol_proj from 'ol/proj';
import LayerSwitcher from 'ol-layerswitcher';

import { onMounted, reactive, ref, computed, watch, toRef, getCurrentInstance } from 'vue';
import { dom } from '@fortawesome/fontawesome-svg-core';

import { domainStore } from '@/store/domain-store';

// {inherits as ol_inherits } from 'ol' deprecated in ol6, this is a workaround:
// https://gis.stackexchange.com/questions/324606/ol-inherits-in-openlayers-6
const ol_inherits = function (child, parent) {
  child.prototype = Object.create(parent.prototype);
  child.prototype.constructor = child;
};

import { axiosBasic, axiosWdwDomain, axiosWdw } from '@/api/axios';

export default {
  name: 'ol-map',

  props: {
    config: {
      required: true,
      type: Object,
    },
    domainid: {
      required: true,
      type: Number,
    },
    initial_lat: {
      required: false,
      type: Number,
      default: 0,
    },
    initial_lon: {
      required: false,
      type: Number,
      default: 0,
    },
    initial_zoom: {
      required: false,
      type: Number,
      default: 0,
    },
    showMarkerAtInitialPosition: {
      required: false,
      type: String,
      default: '',
    },
    searchaddresssuffix: {
      required: false,
      type: String,
      default: '',
    },
    height: {
      type: Number,
      default: 450,
    },
    sizeChanged: {
      type: Number,
      default: 0,
    },
    refreshMarkers: {
      type: Number,
      default: 0,
    },
    setMarkerAllowed: {
      type: Boolean,
      default: false,
    },
    showPopupAllowed: {
      type: Boolean,
      default: true,
      required: false,
    },
    hideNewMarkerLayer: {
      type: Boolean,
      default: true,
    },
    filterParams: {
      required: false,
      type: Object,
      default() {
        return {};
      },
    },
    customButtonRenderer: {
      required: false,
      type: String,
      default: '',
    },
    useDeviceLocation: {
      required: false,
      type: Boolean,
      default: false,
    },
    emitWarningInstead: {
      // If true, any warning will be emitted to parent component instead being displayed here
      required: false,
      type: Boolean,
      default: false,
    },
  },
  emits: ['open-feature', 'new-marker-position', 'warn-marker-position'],

  data() {
    return {
      mapLocateDefaulZoom: 16,
    };
  },

  setup(props, context) {
    // props
    const center_lat = toRef(props, 'initial_lat');
    const center_lon = toRef(props, 'initial_lon');
    const center_zoom = toRef(props, 'initial_zoom');
    const center_custom_marker_uri = toRef(props, 'showMarkerAtInitialPosition');
    const sizeChanged = toRef(props, 'sizeChanged');
    const refreshMarkers = toRef(props, 'refreshMarkers');
    const setMarkerAllowed = toRef(props, 'setMarkerAllowed');
    const showPopupAllowed = toRef(props, 'showPopupAllowed');
    const hideNewMarkerLayer = toRef(props, 'hideNewMarkerLayer');
    const filterParams = toRef(props, 'filterParams');
    const useDeviceLocation = toRef(props, 'useDeviceLocation');
    const emitWarningInstead = toRef(props, 'emitWarningInstead');
    // data
    const map = ref({});
    const layerSources = reactive([]);
    const markerLayerSources = reactive([]); // Store marker layers, so we can reload them once the map is moved or zoomed
    const layers = reactive([]);

    const baseProjection = 'EPSG:4326';
    const layerProjection = 'EPSG:3857';

    const clickMarkerLayer = ref({}); // Holds a vector layer, if a marker was clicked
    const renderer = ref('Default'); // unless otherwise specified
    const mapClickUri = ref(null); // This url will be called on map click
    const newMarkerLayer = ref({}); // Holds a vector layer, if a new marker was placed on map
    const markerName = ref('marker-created-new.png');
    const popupContainer = ref(null); // Holds the popupContainer element
    const popup = ref(null); // Holds the ol overlay popup element
    const openedFeature = ref(null);

    const wmsZoomLevels = ref([]);

    // html elem
    const wdwMap = ref(null);

    // use component instance uid as unique id, in case of multiple components
    const uid = getCurrentInstance().uid;
    // computed
    const id = computed(() => {
      return `wdw-map-${props.domainid}-${uid}`;
    });
    const popupId = computed(() => {
      return `${id.value}-balloon`;
    });
    const mapStyle = computed(() => {
      return `height:${props.height}px`;
    });

    // methods
    function handleExistingMarker(markerLayer) {
      map.value.removeLayer(markerLayer);
    }
    function addPopup() {
      popupContainer.value = document.getElementById(popupId.value);

      if (popupContainer.value) {
        popup.value = new ol_overlay({
          autoPan: true,
          autoPanAnimation: {
            duration: 250,
          },
          element: popupContainer.value,
          offset: [0, -16], // Offset the popup by size of the pointing down arrow
          positioning: 'bottom-center',
          stopEvent: true,
        });

        map.value.addOverlay(popup.value);
      } else {
        console.warn(`Element #${popupId.value} not found!`);
      }
    }
    function hidePopup() {
      popup.value.setPosition(undefined);
      openedFeature.value = null;
      return false;
    }
    function emitOpenFeature(featureId) {
      context.emit('open-feature', featureId);
      popup.value.setPosition(undefined);
      openedFeature.value = null;

      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        /* Safari */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE11 */
        document.msExitFullscreen();
      }
    }
    function setPopupContent(innerHTML, feature) {
      popupContainer.value.querySelector('.content').innerHTML = innerHTML;
      const featureId = feature.getId();
      if (props.customButtonRenderer == 'thought' && featureId) {
        // set feature info, to show button to emit event
        openedFeature.value = {
          id: featureId,
          buttonTitle: domainStore.translate('thought.general.title') + ' ansehen',
        };
        // remove the server side rendered button
        const serverButton = popupContainer.value.querySelector('a.btn.infobox-button');
        serverButton.remove();
      }
      // remove close button that was sent in the html from server
      const serverCloseButton = popupContainer.value.querySelector('.content a.close');
      serverCloseButton.remove();

      // remove the server side rendered button if the rendered button is inside a div
      // Example in Marburg's thought_innerhalb.tt infobox
      const serverCloseButtonInsideDiv = popupContainer.value.querySelector('div a.btn.infobox-button');
      if (serverCloseButtonInsideDiv) {
        serverCloseButtonInsideDiv.remove();
      }

      // remove the server side rendered "Neue Idee" button in Marburg's infobox
      const serverNewIdeaBtnInsideDiv = popupContainer.value.querySelector('div a.btn.center-block.map-popover');
      if (serverNewIdeaBtnInsideDiv) {
        serverNewIdeaBtnInsideDiv.remove();
      }
    }
    function getFirstLayerByType(type) {
      let layerFound = undefined;
      const layerGroups = map.value.getLayers().getArray();
      layerGroups.forEach(group => {
        if (group instanceof layer_group) {
          const layers = group.getLayers();
          layers.forEach(layer => {
            const source = layer.getSource();

            if (type == 'wms' && source instanceof source_tilewms) {
              layerFound = layer;
            }
          });
        }
      });

      return layerFound;
    }
    function buildLayerSourceOptions(source = {}) {
      const options = {};

      if (source.crossOrigin !== undefined) {
        options.crossOrigin = !source.crossOrigin ? null : source.crossOrigin;
      }

      if (source.maxZoom !== undefined) {
        options.maxZoom = source.maxZoom;
      }

      if (source.minZoom !== undefined) {
        options.minZoom = source.minZoom;
      }

      layerSources.push(options);
      return options;
    }
    function buildLayerOptions(source = {}) {
      const options = {
        title: source.title || '',
      };

      if (source.base !== undefined && source.base) {
        options.type = 'base';
      }

      if (source.visible !== undefined) {
        options.visible = source.visible ? true : false;
      }

      if (source.opacity !== undefined) {
        options.opacity = source.opacity;
      }

      if (source.groupname !== undefined) {
        options.groupname = source.groupname;
      }

      if (source.type !== undefined) {
        options.wdwType = source.type;
      }

      layers.push(options);

      return options;
    }
    function getIconConfigForMarker() {
      return {
        src: null,
        scale: 0.8,

        // Openlayers sets the anchor of an image to the center
        // by default. The following line sets the anchor to the
        // bottom of the image, which is what we want for markers
        anchor: [0.5, 1],
      };
    }
    function setMarkerLayer(params, customMarkerUri = '') {
      const locationResultEl = document.getElementById('wdw-bms-map-location-result');
      if (locationResultEl) {
        locationResultEl.setAttribute('tabindex', '-1');
        locationResultEl.innerHTML = '';
        locationResultEl.style.visibility = 'hidden';
      }
      if (setMarkerAllowed.value && mapClickUri.value) {
        axiosWdw
          .get(mapClickUri.value, { params })
          .then(response => {
            console.log('mapClickUri not fully implemented');
            console.log(response);
          })
          .catch(err => {
            console.error('Error loading infobox');
            console.log(err);
          });
      } else {
        // First check if a marker already exists
        if (newMarkerLayer.value) {
          map.value.removeLayer(newMarkerLayer.value);
        }

        if (setMarkerAllowed.value) {
          context.emit('new-marker-position', params);
        }

        const markerStyleConfig = getIconConfigForMarker();
        markerStyleConfig.src = props.config.create_marker_uri;

        if (customMarkerUri) {
          markerStyleConfig.src = customMarkerUri;
        }

        const newMarker = new layer_vector({
          source: new source_vector({
            features: [
              new ol_feature({
                geometry: new geom_point(ol_proj.fromLonLat([params.lon, params.lat])),
                name: 'Neuer Marker',
              }),
            ],
          }),

          style: new ol_style({
            image: new ol_icon(markerStyleConfig),
          }),
        });
        newMarkerLayer.value = newMarker;
        map.value.addLayer(newMarker);
      }
    }
    function addMarker(coordinates) {
      const marker = new ol_feature({
        name: 'Marker',
        geometry: new geom_point(coordinates),
      });

      const vectorSource = new source_vector({
        features: [marker],
      });

      const vectorLayer = new layer_vector({
        source: vectorSource,
      });

      const markerLayer = clickMarkerLayer.value;

      if (markerLayer) {
        handleExistingMarker(markerLayer);
      }

      clickMarkerLayer.value = vectorLayer;

      map.value.addLayer(vectorLayer);
    }
    function handleClickOnMap(e) {
      popup.value.setPosition(undefined);
      openedFeature.value = null;

      if (!setMarkerAllowed.value) {
        return;
      }

      const coordinatesTransform = ol_proj.transform(e.coordinate, layerProjection, baseProjection);

      const params = {
        lat: coordinatesTransform[1],
        lon: coordinatesTransform[0],
      };
      setMarkerLayer(params);
      const locationResultEl = document.getElementById('wdw-bms-map-location-result');
      if (locationResultEl) {
        locationResultEl.innerHTML = '';
        locationResultEl.style.visibility = 'hidden';
      }
    }
    function handleClickOnFeature(e, feature, layer, coordinate) {
      const infoboxUri = feature.get('infobox_uri');
      if (infoboxUri) {
        const params = {
          renderer: renderer.value,
        };

        if (showPopupAllowed.value) {
          axiosWdwDomain
            .get(infoboxUri, { params })
            .then(response => {
              setPopupContent(response.data.data.html, feature);
              popup.value.setPosition(e.coordinate);
            })
            .catch(err => {
              console.error('Error loading infobox');
              console.log(err);
            });
        }
      } else if (layer.get('infobox_uri')) {
        // Per T17350, check feature parameters and coordinate if layer has infobox_uri
        openedFeature.value = null;
        // Build parameters
        const layerInfoboxUri = layer.get('infobox_uri');
        const coordTransform = ol_proj.transform(coordinate, layerProjection, baseProjection);
        const params = {
          renderer: renderer.value,
          lat: coordTransform[1],
          lon: coordTransform[0],
        };
        if (layer !== null) {
          params.layer = layer.get('title');
        }
        axiosWdwDomain
          .get(layerInfoboxUri, { params })
          .then(response => {
            if (showPopupAllowed.value) {
              setPopupContent(response.data.data.html, feature);
              popup.value.setPosition(coordinate);
            }
            /**
             * Per T17350,
             *    if the user clicks on a feature while creating new thought,
             *    it should emit the new-marker-position event so the coordinate can be captured by the form along with other additional data.
             *
             *    For now, this only applies on feature in a layer with the given infobox_uri due to the fact that additional data is passed
             *    together with rendered HTML.
             * */
            if (setMarkerAllowed.value) {
              // Set the marker
              const additional_data = response.data.data.infobox_data
                ? response.data.data.infobox_data.create_defaults
                : {};
              setMarkerLayer({ lat: params.lat, lon: params.lon, additional_data: additional_data });
            }
          })
          .catch(err => {
            console.error('Error loading infobox');
            console.log(err);
          });
      } else {
        handleClickOnMap(e);
      }
    }
    function handleClick(e) {
      // Avoid popups when links are clicked
      const clickedElement = e.originalEvent.target;
      const clickedElementTagName = clickedElement.nodeName.toLowerCase();

      if (clickedElementTagName == 'a') {
        return;
      }

      const featureLayer = map.value.forEachFeatureAtPixel(e.pixel, function (feature, layer) {
        return { feature: feature, layer: layer };
      });
      const feature = featureLayer ? featureLayer.feature : null;
      const layer = featureLayer ? featureLayer.layer : null;

      // when layer is clustered, we need to take an extra step to get the single feature
      if (props.config.cluster_markers && feature && feature.get('features') && feature.get('features').length == 1) {
        feature = feature.get('features')[0];
      }

      if (feature) {
        handleClickOnFeature(e, feature, layer, e.coordinate);
      } else {
        handleClickOnMap(e);
      }
    }
    function getMapExtent() {
      const mapExtent = map.value.getView().calculateExtent(map.value.getSize());

      // [minx, miny, maxx, maxy]
      return ol_proj.transformExtent(mapExtent, layerProjection, baseProjection);
    }
    /**
     * helper method to get mdv specific values for template placeholders like {colMin}
     * @inner
     * @param {L.Point} tilePoint point that specifies a specific tile of the map
     * @returns {Object} dictionary containing values for colMin, colMax, rowMin and rowMax
     */
    function get_mdv_path_elems(x, y) {
      const colMin = Math.floor(x / 100) * 100,
        rowMin = Math.floor(y / 100) * 100;

      return {
        colMin: colMin,
        colMax: colMin + 99,
        rowMin: rowMin,
        rowMax: rowMin + 99,
      };
    }
    function centerMapAtPosition(params) {
      const coordinatesTransform = ol_proj.transform([params.lon, params.lat], baseProjection, layerProjection);

      const view = map.value.getView();
      view.setCenter(coordinatesTransform);

      if (params.zoom !== undefined) {
        view.setZoom(params.zoom);
      }
    }
    function recenterMapToConfig() {
      const params = {
        lat: props.config.lat,
        lon: props.config.lon,
        zoom: props.config.zoom,
      };
      centerMapAtPosition(params);
    }
    function registerListeners() {
      map.value.on('click', e => {
        handleClick(e);
      });
      // map.value.on('dragend', debounce(reloadMarkers, 1000));
    }
    function getLoaderFunction(url, baseProjection, layerProjection, params) {
      return function () {
        axiosWdw.get(url, { params }).then(response => {
          const features = new GeoJSON().readFeatures(response.data, {
            dataProjection: baseProjection,
            featureProjection: layerProjection,
          });
          this.addFeatures(features);
        });
      };
    }

    function mapLocate() {
      const protocol = location.protocol.slice(0, -1);

      if (protocol === 'https') {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            position => {
              const params = {
                lat: position.coords.latitude,
                lon: position.coords.longitude,
              };

              if (setMarkerAllowed.value) {
                // set the marker layer
                //return clicked events?
                setMarkerLayer(params);
                centerMapAtPosition(params);
              } else {
                if (center_zoom.value > 0) {
                  params.zoom = center_zoom.value;
                }
                centerMapAtPosition(params);
              }
              // Bus.emit('wdw-map-current-coordinates', {
              //     lon: position.coords.longitude,
              //     lat: position.coords.latitude,
              //     zoom: this.mapLocateDefaulZoom,
              // });

              // Do reverse geocoding
              let url = 'domain/' + props.domainid + '/address_from_latlon?';
              url += 'lat=' + position.coords.latitude;
              url += '&lon=' + position.coords.longitude;

              axiosWdw
                .get(url)
                .then(response => {
                  console.log('todo map location result elem');
                  console.log('TODO refactor jquery stuff...');

                  // Build warn text
                  let warnText = '';
                  if (response.data && response.data.data && response.data.data !== '') {
                    warnText =
                      'Der Marker wurde an der ' +
                      response.data.data +
                      ' platziert. Bitte korrigieren Sie Ihre Eingabe/den Marker falls die Meldung an einer anderen Position platziert werden soll.';
                  } else {
                    warnText =
                      'Die Standortermittlung liefert keine gültige Adresse. Bitte korrigieren Sie Ihre Eingabe/den Marker falls die Meldung an einer anderen Position platziert werden soll.';
                  }

                  // Check whether to display warning here or emit to parent
                  if (emitWarningInstead.value) {
                    context.emit('warn-marker-position', { text: warnText });
                  } else {
                    const warnElem = document.getElementById('wdw-bms-map-location-result');
                    warnElem.innerHTML = "<p class='alert alert-warning'>" + warnText + '</p>';
                    warnElem.style.visibility = 'visible';
                  }
                })
                .catch(err => {
                  // Uncomment if you want to test warning display
                  /*
                  const warnText =
                      'Der Marker wurde an der Test Standort 12345' +
                      ' platziert. Bitte korrigieren Sie Ihre Eingabe/den Marker falls die Meldung an einer anderen Position platziert werden soll.';
                  context.emit("warn-marker-position", { text: warnText });
                  */
                  console.error('Error reversegeocoding user location:');
                  console.log(err);
                });
            },
            error => {
              const errorTypes = {
                0: 'Unbekannter Fehler',
                1: 'Die Geoposition wurde vom Nutzer nicht freigegeben',
                2: 'Geoposition nicht verfügbar',
                3: 'Request timeout',
              };

              let errorMessage = errorTypes[error.code];

              if (error.code == 0 || error.code == 2) {
                errorMessage += ': ' + error.message;
              }

              console.error(errorMessage);

              // On error, go to center position of the given suffix or 0,0 if no suffix
              if (error.code == 2 || error.code == 3) {
                if (this.searchaddresssuffix == '') {
                  // No suffix? Go to 0,0
                  // Bus.emit('wdw-map-current-coordinates', { lon: 0, lat: 0, zoom: this.mapLocateDefaulZoom });
                } else {
                  // Has suffix? Automatically initiate address search
                  // Bus.emit('wdw-trigger-address-search', { query: this.searchaddresssuffix });
                }
              }
            }
          );
        } else {
          console.warn('Geolocation support is not available.');
        }
      }
    }

    // function triggered on change:visible event for map_overlay layers
    function onVisibleLayer(evt) {
      const layer = evt.target;

      // if layer was hidden becomes visible
      if (evt.oldValue == false && layer.get(evt.key) == true) {
        // if exclusiveVisibility is set, hide all other layers with same groupname
        const exclusive_visibility = layer.get('exclusiveVisibility');
        if (exclusive_visibility) {
          const groupname = layer.get('groupname');
          const title = layer.get('title');

          if (groupname !== undefined) {
            map.value.getLayers().forEach(function (l) {
              if (l instanceof layer_group) {
                // layer group
                l.getLayers().forEach(function (subl) {
                  if (subl.get('title') != title && subl.get('groupname') == groupname) {
                    subl.setVisible(0);
                  }
                });
              } else {
                // Individual layer
                if (l.get('title') != title && l.get('groupname') == groupname) {
                  l.setVisible(0);
                }
              }
            });
          }
        }

        // if callback for onVisible is defined, try executing it.
        const callback = layer.get('onVisibleCallback');
        if (callback !== undefined) {
          const func = new Function('return ' + callback)();
          func;
        }
      }
    }

    function initMap() {
      const layers = [];
      let layer;
      const layerGroupsJs = [];
      const layerGroups = [];

      if (props.config.renderer !== undefined) {
        renderer.value = props.config.renderer;
      }
      if (props.config.mapClickUri !== undefined) {
        mapClickUri.value = props.config.mapClickUri;
      }

      props.config.sources.forEach(source => {
        const groupname = source.group;
        const attribution = source.attribution;
        const layerSourceOptions = buildLayerSourceOptions(source);
        const layerOptions = buildLayerOptions(source);

        if (groupname !== undefined) {
          layerOptions.groupname = groupname;
        }
        if (attribution !== undefined) {
          layerSourceOptions.attributions = attribution;
        }

        if (source.type == 'map_overlay') {
          if (source.url !== undefined) {
            layerSourceOptions.url = source.url;
          } else {
            console.warn('Url not defined in source, but this param is mandatory!');
          }
          layerOptions.onVisibleCallback = source.onVisibleFunction || null;
          layerOptions.exclusiveVisibility = source.exclusive_visibility ? true : false;

          layerOptions.source = new source_tileimage(layerSourceOptions);

          layer = new layer_tile(layerOptions);
          layers.push(new layer_tile(layerOptions));
          if (layerOptions.exclusiveVisibility || layerOptions.onVisibleCallback) {
            layer.on('change:visible', onVisibleLayer);
          }
        } else if (source.type == 'marker_overlay') {
          const styleCache = {};

          const styleFunction = function (feature) {
            const marker_uri = feature.get('marker_uri');

            if (!styleCache[marker_uri]) {
              const markerStyleConfig = getIconConfigForMarker();
              markerStyleConfig.src = marker_uri;

              styleCache[marker_uri] = new ol_style({
                image: new ol_icon(markerStyleConfig),
              });
            }

            return [styleCache[marker_uri]];
          };

          layerSourceOptions.format = new GeoJSON();

          let features;

          if (source.url) {
            const source_url = source.url;

            // TODO -> if we wanna fetch thoughts  dynamically on moving map we have to do it here
            // [minx, miny, maxx, maxy]
            // const mapExtent = getMapExtent();
            // const params = {
            //     minLon: mapExtent[0],
            //     minLat: mapExtent[1],
            //     maxLon: mapExtent[2],
            //     maxLat: mapExtent[3],
            // }
            let params = {};
            if (source.forceParams && typeof source.forceParams === 'object') {
              params = source.forceParams;
            }
            // remove rows, page & sort params if they exist
            for (const [key, value] of Object.entries(filterParams.value)) {
              if (key !== 'rows' && key !== 'sort' && key !== 'page') {
                Object.assign(params, { [key]: value });
              }
            }

            layerSourceOptions.loader = getLoaderFunction(source_url, baseProjection, layerProjection, params);

            markerLayerSources.push({
              title: source.title,
              source_url: source_url,
              projection: baseProjection,
              options: layerSourceOptions,
              forceParams: source.forceParams,
            });
          } else if (features) {
            layerSourceOptions.features = new GeoJSON().readFeatures(features, {
              dataProjection: baseProjection,
              featureProjection: layerProjection,
            });
          } else {
            console.warn('neither url nor features defined for layer ' + source.title);
          }

          if (props.config.cluster_markers) {
            // use a cluster on layer-source
            const source = new source_vector(layerSourceOptions);
            const clusterSource = new source_cluster({
              distance: 50,
              source: source,
            });

            layerOptions.source = clusterSource;
            const styleCacheCluster = {};
            const clusterStyleFunction = function (feature) {
              const size = feature.get('features').length;
              let style = styleCacheCluster[size];
              if (!style) {
                style = new ol_style({
                  image: new ol_circle({
                    radius: 12,
                    stroke: new ol_stroke({
                      color: '#fff',
                    }),
                    fill: new ol_fill({
                      color: '#398C8C',
                    }),
                  }),
                  text: new ol_text({
                    text: size.toString(),
                    font: 'bold 12px sans-serif',
                    fill: new ol_fill({
                      color: '#fff',
                    }),
                  }),
                });
                styleCacheCluster[size] = style;
              }
              return style;
            };

            const getStyle = function (feature) {
              if (feature.get('features') && feature.get('features').length > 1) {
                return clusterStyleFunction(feature);
              } else if (feature.get('features') && feature.get('features').length == 1) {
                return styleFunction(feature.get('features')[0]);
              }
              return styleFunction(feature);
            };
            layerOptions.style = getStyle;
            layerOptions.wdwUsingCluster = true;
          } else {
            layerOptions.source = new source_vector(layerSourceOptions);
            layerOptions.style = styleFunction;
          }

          layers.push(new layer_vector(layerOptions));
          layer = new layer_vector(layerOptions);
        } else if (source.type == 'map_overlay_wms') {
          if (source.url !== undefined) {
            layerSourceOptions.url = source.url;
          } else {
            console.warn('Url not defined in source, but this param is mandatory!');
          }

          if (source.wmsParams !== undefined) {
            layerSourceOptions.params = source.wmsParams;
            // T17185 required for specific wms only using this projection
            if (source.wmsParams.projection === 'EPSG:4326') {
              layerSourceOptions.projection = ol_proj.get('EPSG:4326');
            }
          }

          if (source.wmsZoomLevels !== undefined) {
            wmsZoomLevels.value = source.wmsZoomLevels;
          }

          layerOptions.source = new source_tilewms(layerSourceOptions);
          layers.push(new layer_tile(layerOptions));
          layer = new layer_tile(layerOptions);
        } else if (source.type == 'map_overlay_xyz') {
          let baseUrl = '';
          if (source.url !== undefined) {
            baseUrl = source.url;
          } else {
            console.warn('Url not defined in source, but this param is mandatory!');
          }

          if (source.xyzParams !== undefined) {
            layerSourceOptions.params = source.xyzParams;
          }

          if (source.xyzParams.xyzTileFunction === 'RadroutenplanerMVV') {
            layerSourceOptions.tileUrlFunction = function (coord) {
              const zoom = coord[0];
              const x = coord[1];
              const y = coord[2];

              const path_elems = get_mdv_path_elems(x, y);
              const z = zoom - 3;

              const uri =
                baseUrl +
                '/zoomlevel' +
                z +
                '/columns' +
                path_elems.colMin +
                '-' +
                path_elems.colMax +
                '/column' +
                x +
                '/rows' +
                path_elems.rowMin +
                '-' +
                path_elems.rowMax +
                '/' +
                y +
                '_' +
                x +
                '_' +
                z +
                '.png';
              return uri;
            };
          } else {
            console.warn('map type xyz with unhandled xyzTileFunction');
          }

          layerOptions.source = new source_xyz(layerSourceOptions);
          layers.push(new layer_tile(layerOptions));
          layer = new layer_tile(layerOptions);
        } else if (source.type == 'geojson_overlay') {
          const styleCache = {};
          const styleFunction = function (feature) {
            const id = feature.get('id');
            if (!styleCache[id]) {
              const styleObj = {};
              const sourceStyle = source.style;

              if (Object.prototype.hasOwnProperty.call(sourceStyle, 'fill')) {
                styleObj.fill = new ol_fill(sourceStyle.fill);
              }

              if (Object.prototype.hasOwnProperty.call(sourceStyle, 'stroke')) {
                styleObj.stroke = new ol_stroke(sourceStyle.stroke);
              }

              styleCache[id] = new ol_style(styleObj);
            }
            return [styleCache[id]];
          };

          layerSourceOptions.format = new GeoJSON();
          layerOptions.style = styleFunction;

          if (source.url) {
            layerSourceOptions.loader = function () {
              const format = new GeoJSON();

              axiosBasic.get(source.url).then(response => {
                const features = new GeoJSON().readFeatures(response.data, {
                  dataProjection: baseProjection,
                  featureProjection: layerProjection,
                });
                this.addFeatures(features);
              });
            };
          } else {
            console.log('Url is not defined for geojson layer ' + source.title);
          }
          layerOptions.onVisibleCallback = source.onVisibleFunction || null;
          layerOptions.exclusiveVisibility = source.exclusive_visibility ? true : false;
          layerOptions.infobox_uri = source.infobox_uri || null;

          layerOptions.source = new source_vector(layerSourceOptions);
          layer = new layer_vector(layerOptions);
          layers.push(layer);

          if (layerOptions.exclusiveVisibility || layerOptions.onVisibleCallback) {
            layer.on('change:visible', onVisibleLayer);
          }
        }

        let layerGroup = undefined;

        layerGroupsJs.forEach(function (group) {
          if (group.title == groupname) {
            layerGroup = group;
          }
        });

        if (layerGroup !== undefined) {
          layerGroup.layers.push(layer);
        } else {
          const newLayerGroup = {
            title: groupname,
            layers: [layer],
          };
          layerGroupsJs.push(newLayerGroup);
        }
      });

      // Convert to layer group objects
      layerGroupsJs.forEach(function (group) {
        layerGroups.push(new layer_group(group));
      });

      const viewOptions = {
        center: ol_proj.fromLonLat([props.config.lon, props.config.lat]),
        zoom: props.config.zoom,
      };

      if (props.config.bounds !== undefined) {
        if (props.config.bounds.length == 4) {
          viewOptions.extent = ol_proj.transformExtent(props.config.bounds, baseProjection, layerProjection);
        }
        if (props.config.maxZoom) {
          viewOptions.maxZoom = props.config.maxZoom;
        }
        if (props.config.minZoom) {
          viewOptions.minZoom = props.config.minZoom;
        }
      }

      const mapConfig = {
        target: id.value,
        layers: layerGroups,
        controls: ol_control.defaults().extend([
          new ol_control.FullScreen({
            tipLabel: 'Vollbild',
          }),
          new ol_control.ScaleLine({
            units: 'metric',
          }),
        ]),
        view: new ol_view(viewOptions),
      };

      // Only show currentPositionControl this control if protocol is https
      const protocol = location.protocol.slice(0, -1);
      if (useDeviceLocation.value && protocol === 'https') {
        const currentPositionControl = function (opt_options) {
          const options = opt_options || {};

          const button = document.createElement('button');
          button.innerHTML = '<i class="fa fa-map-marker-alt"></i>';
          button.title = 'Aktuellen Standort anzeigen';

          const handleCurrentPosition = function () {
            mapLocate();
          };

          button.addEventListener('click', handleCurrentPosition, false);
          button.addEventListener('touchstart', handleCurrentPosition, false);

          const element = document.createElement('div');
          element.className = 'map-locate ol-unselectable ol-control ol-position';
          element.appendChild(button);

          ol_control.Control.call(this, {
            element: element,
            target: options.target,
          });
        };

        ol_inherits(currentPositionControl, ol_control.Control);

        mapConfig.controls.extend([new currentPositionControl()]);
      }
      // }

      map.value = new ol_map(mapConfig);

      const layerSwitcher = new LayerSwitcher();
      map.value.addControl(layerSwitcher);

      addPopup();
      registerListeners();

      // Bus.emit('wdwmap-created', { map: this.map });
      // Bus.emit('wdwmap-map-locate-check');

      // If initial lat-lon is given, process it as if the user clicks on it
      if (
        props.initial_lat !== null &&
        props.initial_lon !== null &&
        props.initial_lat !== 0 &&
        props.initial_lon !== 0
      ) {
        const data = { lat: props.initial_lat, lon: props.initial_lon };
        if (props.initial_zoom > 0) {
          data.zoom = props.initial_zoom;
        }
        setMarkerLayer(data);
        centerMapAtPosition(data);
      }
    }

    onMounted(() => {
      initMap();
      const newLayerSwitcherButton = document.createElement('i');
      newLayerSwitcherButton.classList.add('fas', 'fa-layer-group');
      const layerSwitcherButton = document.getElementsByClassName('layer-switcher').item(0).firstChild;
      layerSwitcherButton.appendChild(newLayerSwitcherButton);

      // hide map-location-results
      const locationResultEl = document.getElementById('wdw-bms-map-location-result');
      if (locationResultEl) {
        locationResultEl.setAttribute('tabindex', '-1');
        locationResultEl.innerHTML = '';
        locationResultEl.style.visibility = 'hidden';
      }

      // watch dom to replace <i> elements with <svg> elements in map context (fontawesome)
      // limit to required context so as not to influence any elements outside this scope
      // required for location-icon in map
      dom.i2svg({ node: document.getElementById(id.value) });
    });

    watch([center_lat, center_lon], ([lat, lon], [prevLat, prevLon]) => {
      if (lat !== prevLat || lon !== prevLon) {
        if (lat !== null && lat !== 0 && lon !== null && lon !== 0) {
          // lat/lon was provided a new value, so zoom map to that
          const params = { lat: lat, lon: lon };
          if (center_zoom.value > 0) {
            params.zoom = center_zoom.value;
          }
          centerMapAtPosition(params);

          // if custom marker uri is provided for in addition to these params, show it
          if (center_custom_marker_uri.value) {
            setMarkerLayer(params, center_custom_marker_uri.value);
          } else if (newMarkerLayer.value) {
            map.value.removeLayer(newMarkerLayer.value);
          }
        } else {
          // lat/lon was reset to 0/0 center map back to default position
          recenterMapToConfig();

          // also reset marker layer
          map.value.removeLayer(newMarkerLayer.value);
        }
      }
    });

    watch(sizeChanged, (newVal, oldVal) => {
      if (newVal !== oldVal && newVal !== 0 && map.value) {
        setTimeout(function () {
          map.value.updateSize();
        }, 200);
      }
    });

    watch(refreshMarkers, (newVal, oldVal) => {
      if (newVal !== oldVal && newVal !== 0 && map.value) {
        // loop all map layers
        const layerGroups = map.value.getLayers().getArray();
        layerGroups.forEach(group => {
          if (group instanceof layer_group) {
            const layers = group.getLayers();
            layers.forEach(layer => {
              const source = layer.getSource();

              if (source instanceof source_vector && layer.get('wdwType') == 'marker_overlay') {
                layer.getSource().refresh();
                map.value.updateSize();
              }
            });
          }
        });
      }
    });

    watch(filterParams, (newVal, oldVal) => {
      if (!newVal !== null && typeof newVal === 'object' && map.value) {
        // loop all map layers
        const layerGroups = map.value.getLayers().getArray();
        layerGroups.forEach(group => {
          if (group instanceof layer_group) {
            const layers = group.getLayers();
            layers.forEach(layer => {
              const source = layer.getSource();

              if (source instanceof source_vector && layer.get('wdwType') == 'marker_overlay') {
                markerLayerSources.forEach(markerSource => {
                  if (layer.get('title') === markerSource.title) {
                    const url = markerSource.source_url;
                    let params = {};
                    if (markerSource.forceParams && typeof markerSource.forceParams === 'object') {
                      // get forceParams object copy, to ensure not changing original reference
                      params = JSON.parse(JSON.stringify(markerSource.forceParams));
                    }
                    // remove rows, page & sort params if they exist
                    for (const [key, value] of Object.entries(filterParams.value)) {
                      if (key !== 'rows' && key !== 'sort' && key !== 'page') {
                        Object.assign(params, { [key]: value });
                      }
                    }

                    markerSource.options.loader = getLoaderFunction(url, baseProjection, layerProjection, params);
                    const new_source = new source_vector(markerSource.options);
                    const orig_source = layer.getSource();
                    if (layer.get('wdwUsingCluster') && typeof orig_source.getSource == 'function') {
                      // is a cluster. update source on the cluster instead
                      // hierarchy: layer -> cluster source -> vector source
                      orig_source.setSource(new_source);
                    } else {
                      // not a cluster, normally update the layer source
                      // hierarchy: layer -> vector source
                      layer.setSource(new_source);
                    }
                    map.value.updateSize();
                  }
                });
              }
            });
          }
        });
      }
    });

    watch(hideNewMarkerLayer, (newVal, oldVal) => {
      if (newVal && newMarkerLayer.value) {
        map.value.removeLayer(newMarkerLayer.value);
      }
    });

    return {
      id,
      popupId,
      map,
      mapStyle,
      wdwMap,
      openedFeature,
      emitOpenFeature,
      hidePopup,
    };
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/styles/utils/mixins.scss';

.wdw-map-balloon {
  @include clearfix();
  background-color: #fff;
  color: #222;
  border: 1px solid #999;
  max-width: 450px;
  padding: 5px 15px 15px 15px;
  position: relative;
  width: 450px;
  z-index: 10;

  &:after,
  &:before {
    top: 100%;
    left: 50%;
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  &:after {
    border-color: rgba(255, 255, 255, 0);
    border-top-color: #fff;
    border-width: 15px;
    margin-left: -15px;
  }

  &:before {
    border-color: rgba(0, 0, 0, 0);
    border-top-color: #000;
    border-width: 16px;
    margin-left: -16px;
  }

  h1.infobox-header {
    font-size: 1.5rem;
  }

  .close {
    position: absolute;
    right: 7px;
    top: 3px;

    &:before {
      content: '\00d7';
    }
  }
}
</style>
